import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { MileageRate } from "../../api/types";
import { dateShort } from "../../lib/format";

const blank: Partial<MileageRate> = {
  vehicle_type: "car",
  tier_min_miles: 0,
  tier_max_miles: null,
  rate_pence: 45,
  passenger_rate_pence: 5,
  effective_from: new Date().toISOString().slice(0, 10),
  effective_to: null,
  notes: "",
};

const VEHICLE_LABEL: Record<string, string> = {
  car: "Car",
  company_car: "Company car",
  motorbike: "Motorbike",
  bicycle: "Bicycle",
};

const isActive = (r: MileageRate, today = new Date()) => {
  const from = new Date(r.effective_from);
  const to = r.effective_to ? new Date(r.effective_to) : null;
  return from <= today && (!to || to >= today);
};

export default function MileageRatesAdmin() {
  const qc = useQueryClient();
  const { data, isLoading, error: loadError } = useQuery({ queryKey: ["admin-mileage-rates"], queryFn: api.admin.listMileageRates });
  const [editing, setEditing] = useState<Partial<MileageRate> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (m: Partial<MileageRate>) => (m.id ? api.admin.updateMileageRate(m.id, m) : api.admin.createMileageRate(m)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-mileage-rates"] });
      setEditing(null);
      setError(null);
    },
    onError: (e) => setError((e as Error).message),
  });

  const end = useMutation({
    mutationFn: (id: number) => api.admin.endMileageRate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-mileage-rates"] }),
  });

  return (
    <div className="space-y-6">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Back to admin</Link>

      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-liaise-navy">Mileage rates</h1>
          <p className="text-slate-600 mt-1">Pence-per-mile by vehicle type. Tier brackets supported (HMRC 45p/25p style).</p>
        </div>
        <button
          onClick={() => { setEditing({ ...blank }); setError(null); }}
          className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700"
        >
          + New rate
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {isLoading && <div className="px-6 py-12 text-center text-slate-500">Loading…</div>}
        {loadError && (
          <div className="px-6 py-8 text-red-700 bg-red-50 border-b border-red-200 text-sm">
            <div className="font-medium">Failed to load rates</div>
            <div className="font-mono text-xs mt-1">{(loadError as Error).message}</div>
          </div>
        )}
        {data && data.length === 0 && !isLoading && (
          <div className="px-6 py-12 text-center text-slate-500">No rates yet.</div>
        )}
        {data && data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-6 py-3 font-medium">Vehicle</th>
                <th className="px-6 py-3 font-medium">Tier (miles)</th>
                <th className="px-6 py-3 font-medium text-right">Rate</th>
                <th className="px-6 py-3 font-medium text-right">+ passenger</th>
                <th className="px-6 py-3 font-medium">Effective</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((m) => {
                const active = isActive(m);
                return (
                  <tr key={m.id} className={!active ? "text-slate-400" : ""}>
                    <td className="px-6 py-3 font-medium">{VEHICLE_LABEL[m.vehicle_type] ?? m.vehicle_type}</td>
                    <td className="px-6 py-3 text-slate-600">
                      {m.tier_min_miles}{m.tier_max_miles ? `–${m.tier_max_miles}` : "+"}
                    </td>
                    <td className="px-6 py-3 text-right font-medium">{m.rate_pence}p</td>
                    <td className="px-6 py-3 text-right text-slate-600">
                      {m.passenger_rate_pence != null ? `${m.passenger_rate_pence}p` : "—"}
                    </td>
                    <td className="px-6 py-3 text-slate-600 text-xs">
                      <div>From {dateShort(m.effective_from)}</div>
                      {m.effective_to && <div>To {dateShort(m.effective_to)}</div>}
                    </td>
                    <td className="px-6 py-3">
                      {active ? (
                        <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">Active</span>
                      ) : (
                        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Ended</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right space-x-3 whitespace-nowrap">
                      <button onClick={() => { setEditing({ ...m }); setError(null); }} className="text-liaise-navy text-xs hover:underline">Edit</button>
                      {active && (
                        <button onClick={() => end.mutate(m.id)} className="text-red-600 text-xs hover:underline">End today</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold text-liaise-navy">
              {editing.id ? "Edit rate" : "New rate"}
            </h2>

            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block font-medium text-slate-700 mb-1">Vehicle type</span>
                <select
                  value={editing.vehicle_type ?? "car"}
                  onChange={(e) => setEditing({ ...editing, vehicle_type: e.target.value as MileageRate["vehicle_type"] })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                >
                  <option value="car">Car</option>
                  <option value="company_car">Company car</option>
                  <option value="motorbike">Motorbike</option>
                  <option value="bicycle">Bicycle</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="block font-medium text-slate-700 mb-1">Effective from</span>
                <input
                  type="date"
                  value={editing.effective_from ?? ""}
                  onChange={(e) => setEditing({ ...editing, effective_from: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block font-medium text-slate-700 mb-1">Tier min (miles)</span>
                <input
                  type="number"
                  value={editing.tier_min_miles ?? 0}
                  onChange={(e) => setEditing({ ...editing, tier_min_miles: Number(e.target.value) })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                />
              </label>
              <label className="text-sm">
                <span className="block font-medium text-slate-700 mb-1">Tier max (miles)</span>
                <input
                  type="number"
                  value={editing.tier_max_miles ?? ""}
                  onChange={(e) => setEditing({ ...editing, tier_max_miles: e.target.value === "" ? null : Number(e.target.value) })}
                  placeholder="(no upper limit)"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block font-medium text-slate-700 mb-1">Rate (pence/mile)</span>
                <input
                  type="number"
                  value={editing.rate_pence ?? 0}
                  onChange={(e) => setEditing({ ...editing, rate_pence: Number(e.target.value) })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                />
              </label>
              <label className="text-sm">
                <span className="block font-medium text-slate-700 mb-1">Passenger rate (pence/mile)</span>
                <input
                  type="number"
                  value={editing.passenger_rate_pence ?? ""}
                  onChange={(e) => setEditing({ ...editing, passenger_rate_pence: e.target.value === "" ? null : Number(e.target.value) })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                />
              </label>
            </div>

            <label className="text-sm block">
              <span className="block font-medium text-slate-700 mb-1">Effective to (optional)</span>
              <input
                type="date"
                value={editing.effective_to ?? ""}
                onChange={(e) => setEditing({ ...editing, effective_to: e.target.value === "" ? null : e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
              />
            </label>

            <label className="text-sm block">
              <span className="block font-medium text-slate-700 mb-1">Notes</span>
              <input
                type="text"
                value={editing.notes ?? ""}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
                placeholder="e.g. HMRC AMAP 2024-04-06 onwards"
              />
            </label>

            {error && <div className="text-red-600 text-sm">{error}</div>}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => save.mutate(editing)}
                disabled={!editing.vehicle_type || editing.rate_pence == null || !editing.effective_from || save.isPending}
                className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-50"
              >
                {save.isPending ? "Saving…" : "Save"}
              </button>
              <button onClick={() => setEditing(null)} className="text-slate-600 px-4 py-2 hover:underline">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
